/**
 * VireoHR - Firebase Authentication Service
 * Handles authentication with Firebase Auth and user profile from Firestore
 */

import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut as firebaseSignOut,
  onAuthStateChanged,
  User as FirebaseUser,
  setPersistence,
  browserLocalPersistence
} from 'firebase/auth';
import { doc, getDoc, setDoc, collection } from 'firebase/firestore';

import { User, UserRole } from '@/contexts/AuthContext';
import { auth, firestore } from '@/lib/firebase/client';

/**
 * Firestore user document structure
 */
export interface FirestoreUserDoc {
  email: string;
  name: string;
  role: UserRole;
  tenantId?: string;
  tenantName?: string;
  branchIds?: string[];
  createdAt: any;
  updatedAt: any;
}

/**
 * Initialize Firebase Auth persistence
 */
export const initializeAuthPersistence = async () => {
  try {
    await setPersistence(auth, browserLocalPersistence);
  } catch (error) {
    console.error('Failed to set auth persistence:', error);
  }
};

/**
 * Fetch user profile from Firestore
 */
export const fetchUserProfile = async (uid: string): Promise<User | null> => {
  try {
    const userDocRef = doc(firestore, 'users', uid);
    const userDocSnap = await getDoc(userDocRef);

    if (!userDocSnap.exists()) {
      console.warn('User profile not found in Firestore for UID:', uid);
      console.warn('Using mock profile for development. Run setCustomClaims.ts to fix.');

      // Return mock user based on Firebase Auth email
      const firebaseUser = auth.currentUser;
      if (firebaseUser?.email) {
        if (import.meta.env.DEV) {
          return getMockUserProfile(firebaseUser.email, uid);
        }
        return null;
      }
      return null;
    }

    const userData = userDocSnap.data() as FirestoreUserDoc;

    return {
      id: uid,
      email: userData.email,
      name: userData.name,
      role: userData.role,
      tenantId: userData.tenantId,
      tenantName: userData.tenantName,
      branchIds: userData.branchIds
    };
  } catch (error) {
    console.error('Error fetching user profile:', error);
    // Fallback to mock for development
    const firebaseUser = auth.currentUser;
    if (firebaseUser?.email) {
      console.warn('Using mock profile due to Firestore error');
      if (import.meta.env.DEV) {
        return getMockUserProfile(firebaseUser.email, uid);
      }
      throw error;
    }
    throw error;
  }
};

/**
 * Get mock user profile for development (when Firestore is not seeded)
 */
function getMockUserProfile(email: string, uid: string): User {
  const mockProfiles: Record<string, User> = {
    'admin@vireohr.com': {
      id: uid,
      email,
      name: 'Admin User',
      role: 'super_admin',
      tenantId: 'demo-tenant',
      tenantName: 'Demo Business',
      branchIds: ['demo-branch']
    },
    'owner@business.com': {
      id: uid,
      email,
      name: 'Owner User',
      role: 'owner',
      tenantId: 'demo-tenant',
      tenantName: 'Demo Business',
      branchIds: ['demo-branch']
    },
    'manager@business.com': {
      id: uid,
      email,
      name: 'Manager User',
      role: 'manager',
      tenantId: 'demo-tenant',
      tenantName: 'Demo Business',
      branchIds: ['demo-branch']
    },
    'employee@business.com': {
      id: uid,
      email,
      name: 'Employee User',
      role: 'employee',
      tenantId: 'demo-tenant',
      tenantName: 'Demo Business',
      branchIds: ['demo-branch']
    }
  };

  return mockProfiles[email] || {
    id: uid,
    email,
    name: 'Demo User',
    role: 'employee',
    tenantId: 'demo-tenant',
    tenantName: 'Demo Business',
    branchIds: ['demo-branch']
  };
}

/**
 * Sign in with email and password
 * Returns user profile from Firestore
 */
export const signInWithEmail = async (
  email: string,
  password: string
): Promise<User> => {
  try {
    // Sign in with Firebase Auth
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    const firebaseUser = userCredential.user;

    // Fetch user profile from Firestore
    const userProfile = await fetchUserProfile(firebaseUser.uid);

    if (!userProfile) {
      throw new Error('User profile not found. Please contact support.');
    }

    return userProfile;
  } catch (error: any) {
    // Handle Firebase Auth errors
    if (error.code === 'auth/invalid-credential' || error.code === 'auth/user-not-found' || error.code === 'auth/wrong-password') {
      throw new Error('Invalid email or password');
    }
    if (error.code === 'auth/too-many-requests') {
      throw new Error('Too many failed attempts. Please try again later.');
    }
    if (error.code === 'auth/network-request-failed') {
      throw new Error('Network error. Please check your connection.');
    }

    console.error('Sign in error:', error);
    throw error;
  }
};

/**
 * Register new user
 * Creates Firebase Auth user and Firestore profile
 */
export const registerWithEmail = async (
  email: string,
  password: string,
  name: string,
  role: UserRole,
  tenantId?: string,
  tenantName?: string,
  branchIds?: string[]
): Promise<User> => {
  try {
    // Create Firebase Auth user
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    const firebaseUser = userCredential.user;

    // Create Firestore user profile (remove undefined fields)
    const userProfile: any = {
      email,
      name,
      role,
      createdAt: new Date(),
      updatedAt: new Date()
    };

    // Only add optional fields if they exist
    if (tenantId) userProfile.tenantId = tenantId;
    if (tenantName) userProfile.tenantName = tenantName;
    if (branchIds) userProfile.branchIds = branchIds;

    await setDoc(doc(firestore, 'users', firebaseUser.uid), userProfile);

    return {
      id: firebaseUser.uid,
      email,
      name,
      role,
      tenantId,
      tenantName,
      branchIds
    };
  } catch (error: any) {
    // Handle Firebase Auth errors
    if (error.code === 'auth/email-already-in-use') {
      throw new Error('Email already registered');
    }
    if (error.code === 'auth/weak-password') {
      throw new Error('Password is too weak. Use at least 6 characters.');
    }
    if (error.code === 'auth/invalid-email') {
      throw new Error('Invalid email address');
    }

    console.error('Registration error:', error);
    throw error;
  }
};

/**
 * Sign out
 */
export const signOut = async (): Promise<void> => {
  try {
    await firebaseSignOut(auth);
  } catch (error) {
    console.error('Sign out error:', error);
    throw error;
  }
};

/**
 * Listen to auth state changes
 */
export const onAuthStateChange = (
  callback: (user: User | null) => void
): (() => void) => {
  return onAuthStateChanged(auth, async (firebaseUser: FirebaseUser | null) => {
    if (firebaseUser) {
      try {
        const userProfile = await fetchUserProfile(firebaseUser.uid);
        callback(userProfile);
      } catch (error) {
        console.error('Error in auth state change:', error);
        callback(null);
      }
    } else {
      callback(null);
    }
  });
};

/**
 * Get current Firebase user
 */
export const getCurrentFirebaseUser = (): FirebaseUser | null => {
  return auth.currentUser;
};
